import {
  Component,
  EventEmitter,
  Input,
  AfterViewInit,
  Output,
  ViewEncapsulation,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { RoutesKeys } from 'src/app/utils/routes';
import { CarouselPage } from '../../../models/carousel/carousel-page.model';

@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Carousel organism that displays pages one at a time
 */
export class CarouselComponent implements AfterViewInit, OnInit {
  @Input() pages!: CarouselPage[];
  @Input() activePage: number = 1;
  @Input() finishLabel: string = '';
  @Input() previousPageLabel: string = '';
  @Input() nextPageLabel: string = '';
  @Input() anchorText: string = '';
  RoutesKeys = RoutesKeys;
  customOptions!: OwlOptions;

  @Output() finish = new EventEmitter<any>();

  /**
   * @constructor
   */
  constructor(private readonly router: Router) {}

  /**
   * on init
   */
  ngOnInit() {
    this.customOptions = {
      loop: false,
      mouseDrag: true,
      touchDrag: true,
      pullDrag: true,
      dots: true,
      items: 1,
      stagePadding: 100,
      nav: true,
      autoWidth: true,
      navText: [this.previousPageLabel, this.nextPageLabel],
    };
  }

  /**
   * after view init
   */
  ngAfterViewInit() {
    this.setupOwlCarouselFinishButton();
  }

  /**
   * Sets up owl carousel finish button modification
   */
  setupOwlCarouselFinishButton() {
    const nextButton = this.getNextButton();
    const lastDot = this.getLastDot();
    const skipButton = this.getSkipButton();
    if (!nextButton || !lastDot || !skipButton) {
      return;
    }
    const observer = new MutationObserver(
      this.mutationFunction(nextButton, skipButton)
    );
    observer.observe(lastDot, { attributes: true });
  }

  /**
   * Mutation function that reacts to class changes
   * @param {HTMLDivElement} nextButton
   * @param {HTMLDivElement} skipButton
   * @return {function(MutationRecord[]): MutationObserver}
   */
  mutationFunction(nextButton: HTMLDivElement, skipButton: HTMLDivElement) {
    return (mutationList: MutationRecord[]) => {
      mutationList.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          const classList = (mutation.target as any).classList;
          let textContent = '';
          if (classList.contains('active')) {
            nextButton.classList.remove('disabled');
            textContent = this.finishLabel;
            nextButton.onclick = () => {
              this.emitFinish();
            };
            skipButton.classList.add('hidden');
          } else {
            textContent = this.nextPageLabel;
            nextButton.onclick = null;
            skipButton.classList.remove('hidden');
          }
          nextButton.textContent = textContent;
        }
      });
    };
  }

  /**
   * Gets last dot of owl carousel dots
   * @return {Element | undefined}
   */
  getLastDot(): Element | undefined {
    const dots = document.querySelectorAll('.owl-dot');
    if (!dots) {
      return;
    }
    return dots.item(dots.length - 1);
  }

  /**
   * Gets next button of owl carousel
   * @return {HTMLDivElement | undefined}
   */
  getNextButton(): HTMLDivElement | undefined {
    return this.getDivElement('.owl-next');
  }

  /**
   * Gets skip button anchor
   * @return {HTMLDivElement | undefined}
   */
  getSkipButton(): HTMLDivElement | undefined {
    return this.getDivElement('.footer');
  }

  /**
   * Gets some div element
   * @param {string} selector
   * @return {HTMLDivElement | undefined}
   */
  private getDivElement(selector: string): HTMLDivElement | undefined {
    const elements = document.querySelectorAll(selector);
    if (!elements) {
      return;
    }
    return elements.item(elements.length - 1) as HTMLDivElement;
  }

  /**
   * @return {number} number of pages this carousel possesses
   */
  numberOfPages(): number {
    return this.pages.length;
  }

  /**
   * @return {number[]}
   */
  numberOfPagesRange(): number[] {
    return [...Array(this.numberOfPages()).keys()];
  }

  /**
   * This function simplifies getIcon and getText
   * @param {string} type either 'icon' or 'text'
   * @param {number?} i
   * @return {string} icon or that should be displayed in currently active page
   */
  getIconOrText(type: 'icon' | 'text', i?: number): string {
    const index = i != null ? i : this.activePage - 1;
    return this.pages.length > 0 ? this.pages[index][type] : '';
  }

  /**
   * @param {number?} i
   * @return {string} icon that should be displayed in currently active page
   */
  getIcon(i?: number): string {
    return this.getIconOrText('icon', i);
  }

  /**
   * @param {number?} i
   * @return {string} text that should be displayed in currently active page
   */
  getText(i?: number): string {
    return this.getIconOrText('text', i);
  }

  /**
   * emits finish event
   */
  emitFinish() {
    this.finish.emit();
  }
}
